import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { sanitizeFileName } from "utils/sanitizeFileName";
import { buildExportLegend } from "../../utils/buildExportLegend";
import { buildNoteExportEntries } from "../../utils/buildNoteExportEntries";
import { buildRoomExportSections } from "../../utils/buildRoomExportSections";
import { buildGroupCircuitPathsState } from "../../utils/buildGroupCircuitPathsState";
import { exportFloorPlanToPdf } from "../../utils/exportFloorPlanToPdf";
import { buildRoomsOverlay } from "../../utils/rooms";
import {
  buildAppliedAreasOverlay,
  filterExportElementsBySelectionAreas,
  filterExportRouteGroupsBySelectionAreas,
} from "../../utils/selectionAreas";
import type {
  ExportNoteAppendix,
  ExportRoomSections,
} from "../../utils/exportFloorPlanToPdf";
import type { PlanSelectionArea } from "../../utils/types";
import type { UseExportFloorPlanPdf } from "./types";

const EMPTY_DETECTED_OBJECTS: never[] = [];
const EMPTY_SELECTION_AREAS: PlanSelectionArea[] = [];

export const useExportFloorPlanPdf: UseExportFloorPlanPdf = ({
  selectedPlan,
  isCablesView,
  isExecutionMode,
  routeGroups,
  byGroupId,
  rooms,
}) => {
  const { t, i18n } = useTranslation();

  const selectionAreas = selectedPlan?.selectionAreas ?? EMPTY_SELECTION_AREAS;

  // Planning exports apply the selection areas; execution exports the map as seen.
  const exportElementsByGroupId = useMemo(
    () =>
      isExecutionMode
        ? byGroupId
        : filterExportElementsBySelectionAreas({ byGroupId, selectionAreas }),
    [isExecutionMode, byGroupId, selectionAreas],
  );

  const exportRouteGroups = useMemo(
    () =>
      isExecutionMode
        ? routeGroups
        : filterExportRouteGroupsBySelectionAreas({
            routeGroups,
            selectionAreas,
          }),
    [isExecutionMode, routeGroups, selectionAreas],
  );

  const exportRouteGroupsByLabel = useMemo(
    () =>
      Object.fromEntries(
        exportRouteGroups.map((group) => [group.label, group]),
      ),
    [exportRouteGroups],
  );

  const handleExportPdf = useCallback(
    async ({ selectedLabels }: { selectedLabels: string[] }) => {
      if (!selectedPlan) return;

      const safeName = sanitizeFileName({
        rawName: selectedPlan.label ?? "floor-plan",
        fallback: "floor-plan",
      });

      const detectedObjects = isCablesView
        ? EMPTY_DETECTED_OBJECTS
        : selectedLabels.flatMap(
            (label) => exportElementsByGroupId[label] ?? [],
          );

      const selectedRouteGroups = isCablesView
        ? selectedLabels.flatMap((label) =>
            exportRouteGroupsByLabel[label]
              ? [exportRouteGroupsByLabel[label]]
              : [],
          )
        : [];
      const circuitPaths =
        isCablesView && selectedRouteGroups.length > 0
          ? buildGroupCircuitPathsState(selectedRouteGroups)
          : undefined;

      const computedLegend = buildExportLegend({
        isCablesView,
        selectedLabels,
        byGroupId: exportElementsByGroupId,
        byRouteGroupLabel: exportRouteGroupsByLabel,
        t,
      });

      const polygonsOverlay = isExecutionMode
        ? buildRoomsOverlay({ rooms })
        : buildAppliedAreasOverlay({ selectionAreas, isCablesView });

      const roomSections: ExportRoomSections | undefined =
        isExecutionMode && !isCablesView && rooms.length > 0
          ? {
              title: t("smartSurveyor.rooms.exportPageTitle"),
              note: t("smartSurveyor.rooms.exportOverlapNote"),
              legends: buildRoomExportSections({
                rooms,
                selectedLabels,
                byGroupId: exportElementsByGroupId,
                t,
              }),
            }
          : undefined;

      const noteEntries = buildNoteExportEntries({
        detectedObjects,
        locale: i18n.language,
        t,
      });
      const noteAppendix: ExportNoteAppendix | undefined =
        noteEntries.length > 0
          ? {
              title: t("smartSurveyor.elementDetails.note.exportPageTitle"),
              continuedLabel: t(
                "smartSurveyor.elementDetails.note.exportContinued",
              ),
              entries: noteEntries,
            }
          : undefined;

      await exportFloorPlanToPdf({
        pdfUrl: selectedPlan.pdfUrl,
        imageUrl: selectedPlan.planImage,
        pageNumber: 1,
        fileName: `${safeName}.pdf`,
        detectedObjects,
        circuitPaths: circuitPaths ?? undefined,
        polygonsOverlay: polygonsOverlay ?? undefined,
        legend: computedLegend.items.length > 0 ? computedLegend : undefined,
        roomSections,
        noteAppendix,
        rotation: selectedPlan.rotationByPageIndex.get(0) ?? 0,
      });
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_PLAN_PDF_EXPORTED, {
        content: isCablesView ? "routes" : "elements",
        groupCount: selectedLabels.length,
        hasRooms: roomSections !== undefined,
      });
    },
    [
      t,
      i18n.language,
      selectedPlan,
      isCablesView,
      isExecutionMode,
      rooms,
      selectionAreas,
      exportElementsByGroupId,
      exportRouteGroupsByLabel,
    ],
  );

  return { handleExportPdf, exportElementsByGroupId, exportRouteGroups };
};
